export type CustomerReportType =
  | 'best-customers'
  | 'purchase-frequency'
  | 'average-ticket'
  | 'abc-curve'
  | 'at-risk'
  | 'sales-by-neighborhood';

export type CustomerIntelligenceSortDirection = 'asc' | 'desc';

export type CustomerIntelligenceSortKey =
  | 'customer'
  | 'phone'
  | 'email'
  | 'orderCount'
  | 'totalPurchased'
  | 'averageTicket'
  | 'firstPurchase'
  | 'lastPurchase'
  | 'daysWithoutPurchase'
  | 'averageIntervalDays'
  | 'frequencyProfile'
  | 'totalProducts'
  | 'totalDiscounts'
  | 'totalDelivery'
  | 'curve'
  | 'participationPercentage'
  | 'cumulativePercentage'
  | 'risk'
  | 'delayFactor'
  | 'neighborhood'
  | 'customerCount'
  | 'totalSold'
  | 'ordersPerCustomer'
  | 'orderPercentage';

export interface CustomerIntelligenceSort {
  sortBy: CustomerIntelligenceSortKey;
  sortDirection: CustomerIntelligenceSortDirection;
}

export interface CustomerIntelligenceSortColumn {
  sortBy: CustomerIntelligenceSortKey;
  label: string;
}

export const CUSTOMER_INTELLIGENCE_SORT_COLUMNS: Record<
  CustomerReportType,
  readonly CustomerIntelligenceSortColumn[]
> = {
  'best-customers': [
    { sortBy: 'customer', label: 'Cliente' },
    { sortBy: 'phone', label: 'Telefone' },
    { sortBy: 'email', label: 'E-mail' },
    { sortBy: 'orderCount', label: 'Pedidos' },
    { sortBy: 'totalPurchased', label: 'Total' },
    { sortBy: 'averageTicket', label: 'Ticket' },
    { sortBy: 'firstPurchase', label: 'Primeira compra' },
    { sortBy: 'lastPurchase', label: 'Última compra' },
    { sortBy: 'daysWithoutPurchase', label: 'Dias sem comprar' },
  ],
  'purchase-frequency': [
    { sortBy: 'customer', label: 'Cliente' },
    { sortBy: 'phone', label: 'Telefone' },
    { sortBy: 'email', label: 'E-mail' },
    { sortBy: 'orderCount', label: 'Pedidos' },
    { sortBy: 'averageIntervalDays', label: 'Intervalo médio' },
    { sortBy: 'frequencyProfile', label: 'Perfil' },
    { sortBy: 'firstPurchase', label: 'Primeira compra' },
    { sortBy: 'lastPurchase', label: 'Última compra' },
    { sortBy: 'daysWithoutPurchase', label: 'Dias sem comprar' },
  ],
  'average-ticket': [
    { sortBy: 'customer', label: 'Cliente' },
    { sortBy: 'phone', label: 'Telefone' },
    { sortBy: 'email', label: 'E-mail' },
    { sortBy: 'orderCount', label: 'Pedidos' },
    { sortBy: 'totalProducts', label: 'Produtos' },
    { sortBy: 'totalDiscounts', label: 'Descontos' },
    { sortBy: 'totalDelivery', label: 'Entrega' },
    { sortBy: 'totalPurchased', label: 'Total' },
    { sortBy: 'averageTicket', label: 'Ticket' },
  ],
  'abc-curve': [
    { sortBy: 'customer', label: 'Cliente' },
    { sortBy: 'curve', label: 'Curva' },
    { sortBy: 'participationPercentage', label: 'Participação' },
    { sortBy: 'cumulativePercentage', label: 'Acumulado' },
    { sortBy: 'orderCount', label: 'Pedidos' },
    { sortBy: 'totalPurchased', label: 'Total' },
    { sortBy: 'averageTicket', label: 'Ticket' },
    { sortBy: 'lastPurchase', label: 'Última compra' },
  ],
  'at-risk': [
    { sortBy: 'customer', label: 'Cliente' },
    { sortBy: 'curve', label: 'Curva' },
    { sortBy: 'risk', label: 'Risco' },
    { sortBy: 'orderCount', label: 'Pedidos' },
    { sortBy: 'totalPurchased', label: 'Total' },
    { sortBy: 'averageTicket', label: 'Ticket' },
    { sortBy: 'averageIntervalDays', label: 'Intervalo' },
    { sortBy: 'delayFactor', label: 'Atraso' },
    { sortBy: 'daysWithoutPurchase', label: 'Dias sem comprar' },
    { sortBy: 'lastPurchase', label: 'Última compra' },
  ],
  'sales-by-neighborhood': [
    { sortBy: 'neighborhood', label: 'Bairro' },
    { sortBy: 'orderCount', label: 'Pedidos' },
    { sortBy: 'customerCount', label: 'Clientes' },
    { sortBy: 'totalSold', label: 'Total' },
    { sortBy: 'averageTicket', label: 'Ticket' },
    { sortBy: 'ordersPerCustomer', label: 'Pedidos/cliente' },
    { sortBy: 'orderPercentage', label: 'Percentual' },
  ],
};

export type CustomerCurve = 'A' | 'B' | 'C';
export type CustomerRisk = 'CRÍTICO' | 'ALTO' | 'MÉDIO' | 'BAIXO';

export interface CustomerIntelligenceFilters {
  startDate: string;
  endDate: string;
  curve?: CustomerCurve;
  risk?: CustomerRisk;
  daysWithoutPurchase?: number;
}

export interface CustomerRowBase {
  customerId: number;
  customer: string;
  phone: string | null;
  email: string | null;
}

export interface BestCustomerRow extends CustomerRowBase {
  orderCount: number;
  totalPurchased: number;
  averageTicket: number;
  firstPurchase: string | null;
  lastPurchase: string | null;
  daysWithoutPurchase: number;
}

export interface PurchaseFrequencyRow extends CustomerRowBase {
  orderCount: number;
  totalPurchased: number;
  averageTicket: number;
  firstPurchase: string | null;
  lastPurchase: string | null;
  daysWithoutPurchase: number;
  averageIntervalDays: number | null;
  frequencyProfile: string;
}

export interface AverageTicketRow extends CustomerRowBase {
  orderCount: number;
  totalProducts: number;
  totalDiscounts: number;
  totalDelivery: number;
  totalPurchased: number;
  averageTicket: number;
  firstPurchase: string | null;
  lastPurchase: string | null;
  daysWithoutPurchase: number;
}

export interface AbcCurveRow extends CustomerRowBase {
  curve: CustomerCurve;
  participationPercentage: number;
  cumulativePercentage: number;
  orderCount: number;
  totalPurchased: number;
  averageTicket: number;
  lastPurchase: string | null;
}

export interface AtRiskRow extends CustomerRowBase {
  curve: CustomerCurve;
  risk: CustomerRisk;
  orderCount: number;
  totalPurchased: number;
  averageTicket: number;
  averageIntervalDays: number;
  daysWithoutPurchase: number;
  delayFactor: number | null;
  lastPurchase: string | null;
}

export interface SalesByNeighborhoodRow {
  neighborhood: string;
  orderCount: number;
  customerCount: number;
  totalSold: number;
  averageTicket: number;
  ordersPerCustomer: number | null;
  orderPercentage: number;
}

export type CustomerIntelligenceRow =
  | BestCustomerRow
  | PurchaseFrequencyRow
  | AverageTicketRow
  | AbcCurveRow
  | AtRiskRow
  | SalesByNeighborhoodRow;

export interface CustomerIntelligenceResponse {
  data: CustomerIntelligenceRow[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    hasNextPage: boolean;
  };
  appliedFilters: CustomerIntelligenceFilters;
  appliedSort: CustomerIntelligenceSort;
  generatedAt: string;
}

export interface BestCustomersChartRow {
  customer: string;
  orderCount: number;
  totalPurchased: number;
}

export interface PurchaseFrequencyChartRow {
  frequencyProfile: string;
  customerCount: number;
}

export interface AverageTicketChartRow {
  customer: string;
  orderCount: number;
  averageTicket: number;
}

export interface AbcCurveChartRow {
  curve: CustomerCurve;
  customerCount: number;
  totalPurchased: number;
  participationPercentage: number;
}

export interface AtRiskChartRow {
  risk: CustomerRisk;
  curve: CustomerCurve;
  customerCount: number;
}

export interface SalesByNeighborhoodChartRow {
  neighborhood: string;
  orderCount: number;
  totalSold: number;
}

export type CustomerIntelligenceChartResponse =
  | {
      reportType: 'best-customers';
      data: BestCustomersChartRow[];
    }
  | {
      reportType: 'purchase-frequency';
      data: PurchaseFrequencyChartRow[];
    }
  | {
      reportType: 'average-ticket';
      data: AverageTicketChartRow[];
    }
  | { reportType: 'abc-curve'; data: AbcCurveChartRow[] }
  | { reportType: 'at-risk'; data: AtRiskChartRow[] }
  | {
      reportType: 'sales-by-neighborhood';
      data: SalesByNeighborhoodChartRow[];
    };
